import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { allArticles, categories } from "@/mocks/movies";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = allArticles.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 8090电影网"
          description="抱歉，您查找的文章不存在。"
          keywords="8090电影网"
        />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的影视内容不存在或已下架。</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const pageTitle = `${article.title} - 8090电影网怀旧影视`;
  const pageDesc = article.excerpt;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: `${article.year}-01-01`,
    author: {
      "@type": "Organization",
      name: "8090电影网",
    },
    publisher: {
      "@type": "Organization",
      name: "8090电影网",
      logo: {
        "@type": "ImageObject",
        url: "https://8090movie.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://8090movie.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${article.title},${article.year},8090电影网,怀旧影视,经典老片,${category?.name || ""}`}
        canonicalUrl={`https://8090movie.com/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        jsonLd={articleJsonLd}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              ...(category
                ? [{ label: category.name, href: `/category/${category.slug}` }]
                : []),
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Header */}
        <section className="w-full bg-background-50 pb-8">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-100 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                {article.year}
              </span>
              <span className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-star-fill text-accent-500"></i>
                <strong>{article.rating}</strong>
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
              {article.excerpt}
            </p>
            {article.director && (
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>
                  导演: <strong className="text-foreground-800">{article.director}</strong>
                </span>
                {article.cast && article.cast.length > 0 && (
                  <span>
                    主演:{" "}
                    {article.cast.map((actor, idx) => (
                      <span key={actor}>
                        <strong className="text-foreground-800">{actor}</strong>
                        {idx < article.cast.length - 1 && " / "}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Article Image */}
        <section className="w-full bg-background-50 pb-10">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="relative w-full h-64 md:h-96 rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={`${article.title} 经典剧照`}
                title={`${article.title} - 8090电影网怀旧影视`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full bg-white py-10 md:py-14">
          <article className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: (article.content || "")
                  .split("\n")
                  .map((line) => {
                    if (line.startsWith("## ")) {
                      return `<h2 class="text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4">${line.replace("## ", "")}</h2>`;
                    }
                    if (line.startsWith("### ")) {
                      return `<h3 class="text-lg md:text-xl font-semibold text-foreground-900 mt-6 mb-3">${line.replace("### ", "")}</h3>`;
                    }
                    if (line.startsWith("> ")) {
                      return `<blockquote class="border-l-4 border-primary-400 pl-4 py-1 my-4 text-foreground-600 italic bg-primary-50/50 rounded-r-lg">${line.replace("> ", "")}</blockquote>`;
                    }
                    if (line.startsWith("- **")) {
                      const match = line.match(/- \*\*(.+?)\*\*：(.+)/);
                      if (match) {
                        return `<p class="mb-1 pl-4"><strong class="text-foreground-900">${match[1]}</strong>：${match[2]}</p>`;
                      }
                    }
                    if (line.startsWith("- ")) {
                      return `<p class="mb-1 pl-4 text-foreground-700">• ${line.replace("- ", "")}</p>`;
                    }
                    if (line.match(/^\d+\./)) {
                      return `<p class="mb-1 pl-4 text-foreground-700"><strong>${line.match(/^\d+\./)?.[0]}</strong> ${line.replace(/^\d+\.\s*/, "")}</p>`;
                    }
                    if (line.trim() === "") return "<br/>";
                    return `<p class="mb-3 text-foreground-700 leading-relaxed">${line}</p>`;
                  })
                  .join(""),
              }}
            />
          </article>
        </section>

        {/* Tags / Share */}
        <section className="w-full bg-background-50 py-8">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签:</span>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 bg-background-200 text-foreground-700 text-xs rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="px-3 py-1 bg-background-200 text-foreground-700 text-xs rounded-full">
                  {article.year}年
                </span>
                <span className="px-3 py-1 bg-background-200 text-foreground-700 text-xs rounded-full">
                  怀旧影视
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享:</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-foreground-600"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-foreground-600"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-foreground-600"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((related) => (
                  <article
                    key={related.id}
                    className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-300 transition-all"
                  >
                    <Link
                      to={`/article/${related.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="block cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={related.imageUrl}
                          alt={`${related.title} 剧照`}
                          title={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                            {related.year}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="flex items-center gap-1 text-xs text-accent-600">
                            <i className="ri-star-fill text-xs"></i>
                            {related.rating}
                          </span>
                        </div>
                        <h3 className="font-semibold text-foreground-950 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}